import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { registerManualLeague } from '../services/api';
import '../styles/fonts.css';

const inp = {
    display: 'block', width: '100%', boxSizing: 'border-box',
    background: '#161b22', border: '1px solid #30363d', borderRadius: 6,
    color: '#e6edf3', fontSize: '0.9rem', padding: '10px 14px',
    outline: 'none', marginTop: 6, marginBottom: 16,
};
const lbl = { display: 'block', fontSize: '0.82rem', color: '#8b949e', fontWeight: 500 };
const colTitle = {
    fontSize: '0.72rem', fontWeight: 700, color: '#8b949e',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    borderBottom: '1px solid #21262d', paddingBottom: 8, marginBottom: 20,
};

function AddLeagueManual() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();

    const manualToken = searchParams.get('manual_token') || '';
    const custId = searchParams.get('cust_id') || '';
    const displayName = searchParams.get('display_name') || '';

    const [name, setName] = useState('');
    const [about, setAbout] = useState('');
    const [websiteUrl, setWebsiteUrl] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [discordUrl, setDiscordUrl] = useState('');
    const [whatsappUrl, setWhatsappUrl] = useState('');
    const [instagramUrl, setInstagramUrl] = useState('');
    const [twitchUrl, setTwitchUrl] = useState('');
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const logoInputRef = useRef(null);

    if (!manualToken) {
        return (
            <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
                <Header />
                <main style={{ flex: 1, maxWidth: 960, margin: '0 auto', padding: '48px 24px' }}>
                    <p style={{ color: '#e07070' }}>
                        Link inválido ou expirado.{' '}
                        <a href="/add-league" style={{ color: '#58a6ff' }}>Voltar</a>
                    </p>
                </main>
                <Footer />
            </div>
        );
    }

    function handleLogoChange(e) {
        const file = e.target.files[0];
        setLogoPreview(file ? URL.createObjectURL(file) : null);
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        if (password.length < 6) { setError(t('addLeague.passwordTooShort')); return; }
        if (password !== confirmPassword) { setError(t('addLeague.passwordMismatch')); return; }

        const formData = new FormData();
        formData.append('manual_token', manualToken);
        formData.append('name', name);
        formData.append('password', password);
        if (about) formData.append('about', about);
        if (websiteUrl) formData.append('url', websiteUrl);
        if (youtubeUrl) formData.append('youtube_url', youtubeUrl);
        if (discordUrl) formData.append('discord_url', discordUrl);
        if (whatsappUrl) formData.append('whatsapp_url', whatsappUrl);
        if (instagramUrl) formData.append('instagram_url', instagramUrl);
        if (twitchUrl) formData.append('twitch_url', twitchUrl);
        const logoFile = logoInputRef.current?.files?.[0];
        if (logoFile) formData.append('logo_file', logoFile);

        setLoading(true);
        try {
            const result = await registerManualLeague(formData);
            navigate(`/leagues/${result.league_id}/organizer`);
        } catch (err) {
            setError(err?.error || t('addLeague.genericError'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={{ minHeight: '100vh', background: '#1b1f23', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <main style={{ flex: 1, maxWidth: 960, margin: '0 auto', padding: '48px 24px', width: '100%' }}>

                <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#fff', marginBottom: 24 }}>
                    {t('addLeague.manualTitle')}
                </h1>

                {/* Identity badge */}
                <div style={{ background: '#1a2a1a', border: '1px solid #2d4a2d', borderRadius: 8, padding: '14px 18px', marginBottom: 32 }}>
                    <div style={{ color: '#4caf50', fontWeight: 600, fontSize: '0.88rem', marginBottom: 4 }}>
                        ✓ {t('addLeague.manualIdentity')}
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#c9d1d9' }}>
                        {t('addLeague.verifiedAs')}: <strong>{displayName}</strong>
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#8b949e', marginTop: 2 }}>
                        iRacing ID: {custId}
                    </div>
                </div>

                {error && (
                    <div style={{ background: '#3d2020', color: '#e07070', borderRadius: 6, padding: '10px 14px', marginBottom: 20, fontSize: '0.85rem' }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32, alignItems: 'start' }}>

                        {/* Coluna esquerda */}
                        <div>
                            <div style={colTitle}>{t('addLeague.leagueData')}</div>

                            <label style={lbl}>{t('addLeague.manualName')} *</label>
                            <input
                                style={inp}
                                type="text"
                                placeholder="Ex: Liga GT3 Brasil"
                                value={name}
                                onChange={e => setName(e.target.value)}
                                required
                            />

                            <label style={lbl}>{t('addLeague.manualAbout')}</label>
                            <textarea
                                style={{ ...inp, resize: 'vertical', minHeight: 90, fontFamily: 'inherit' }}
                                placeholder={t('addLeague.manualAboutPlaceholder')}
                                value={about}
                                onChange={e => setAbout(e.target.value)}
                                maxLength={500}
                            />

                            <label style={lbl}>{t('addLeague.manualWebsite')}</label>
                            <input
                                style={inp}
                                type="url"
                                placeholder="https://sualiga.com.br"
                                value={websiteUrl}
                                onChange={e => setWebsiteUrl(e.target.value)}
                            />

                            <label style={lbl}>{t('addLeague.manualLogo')}</label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, marginBottom: 6 }}>
                                {logoPreview && (
                                    <img src={logoPreview} alt="preview" style={{ width: 48, height: 48, borderRadius: 6, objectFit: 'cover', border: '1px solid #30363d', flexShrink: 0 }} />
                                )}
                                <label style={{
                                    display: 'inline-flex', alignItems: 'center', gap: 8,
                                    background: '#21262d', border: '1px solid #30363d', borderRadius: 6,
                                    padding: '8px 14px', cursor: 'pointer', fontSize: '0.85rem', color: '#c9d1d9',
                                }}>
                                    <i className="fas fa-upload" style={{ fontSize: 12, color: '#8b949e' }} />
                                    {logoPreview ? t('addLeague.manualLogoChange') : t('addLeague.manualLogoSelect')}
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp"
                                        onChange={handleLogoChange}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                            </div>
                            <p style={{ fontSize: '0.75rem', color: '#8b949e', margin: '4px 0 24px' }}>
                                {t('addLeague.manualLogoHint')}
                            </p>

                            <div style={colTitle}>{t('addLeague.passwordSection')}</div>

                            <label style={lbl}>{t('addLeague.password')} *</label>
                            <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} required />

                            <label style={lbl}>{t('addLeague.confirmPassword')} *</label>
                            <input style={inp} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} required />
                        </div>

                        {/* Coluna direita */}
                        <div>
                            <div style={colTitle}>{t('addLeague.socialSection')}</div>

                            <label style={lbl}>YouTube</label>
                            <input style={inp} type="url" placeholder="https://youtube.com/@sualiga" value={youtubeUrl} onChange={e => setYoutubeUrl(e.target.value)} />

                            <label style={lbl}>Discord</label>
                            <input style={inp} type="url" placeholder="https://discord.gg/..." value={discordUrl} onChange={e => setDiscordUrl(e.target.value)} />

                            <label style={lbl}>WhatsApp</label>
                            <input style={inp} type="url" placeholder="https://chat.whatsapp.com/..." value={whatsappUrl} onChange={e => setWhatsappUrl(e.target.value)} />

                            <label style={lbl}>Instagram</label>
                            <input style={inp} type="url" placeholder="https://instagram.com/sualiga" value={instagramUrl} onChange={e => setInstagramUrl(e.target.value)} />

                            <label style={lbl}>Twitch</label>
                            <input style={inp} type="url" placeholder="https://twitch.tv/sualiga" value={twitchUrl} onChange={e => setTwitchUrl(e.target.value)} />
                        </div>

                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            display: 'block', width: '100%', marginTop: 32,
                            background: '#58a6ff', color: '#0d1117', border: 'none',
                            borderRadius: 6, padding: '12px 0', fontWeight: 700,
                            fontSize: '0.95rem', cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.7 : 1,
                        }}
                    >
                        {loading ? t('common.loading') : t('addLeague.submit')}
                    </button>
                </form>

            </main>
            <Footer />
        </div>
    );
}

export default AddLeagueManual;
